import React, { useState, useMemo } from "react";
import Lang from "../../components/Lang";
import useDBO from "../../hooks/useDBO";
import Loading from "../../Loading";
import Search from "../../components/Search";

/**
 * BookList component displays a list of books with search functionality.
 * Features a professional dashboard-style UI consistent with other pages.
 *
 * @param {Object} props - Component props
 * @returns {JSX.Element} The BookList component
 * @author Forge
 * @date 2026-04-06
 * @version 1.0
 */
export default function BookList(props) {
  const [search, setSearch] = useState("");
  const books = useDBO(`/books`);

  /**
   * Filters books based on the search query.
   * Matches against book name (case-insensitive).
   *
   * @returns {Array} Filtered array of books
   */
  const filteredBooks = useMemo(() => {
    if (!books.get) return [];
    if (!search.trim()) return books.sortBy("name");
    
    const searchLower = search.toLowerCase();
    return books
      .sortBy("name")
      .filter((book) => book.name?.toLowerCase().includes(searchLower));
  }, [books, search]);

  if (books.loading) return <Loading />;

  return (
    <div className="booklist-dashboard">
      {/* Header Section */}
      <div className="booklist-header">
        <div className="booklist-header-title">
          <h1 className="booklist-title">
            <Lang w="bookList" />
          </h1>
          <p className="booklist-subtitle">
            {filteredBooks.length} {filteredBooks.length === 1 ? "book" : "books"} available
          </p>
        </div>
        
        {/* Search Input */}
        <div className="booklist-search-container">
          <Search
            search={search}
            setSearch={setSearch}
            placeholder="Search books..."
            className="booklist-search"
          />
        </div>
      </div>

      {/* Book List Content */}
      <div className="booklist-content">
        {filteredBooks.length === 0 ? (
          <div className="booklist-empty">
            <div className="booklist-empty-icon">📚</div>
            <p className="booklist-empty-text">
              {search.trim() ? "No books match your search" : "No books available"}
            </p>
          </div>
        ) : (
          <div className="booklist-grid">
            {filteredBooks.map((book) => (
              <a
                key={book._id}
                href={book.link}
                target="_blank"
                rel="noopener noreferrer"
                className="booklist-card"
              >
                <div className="booklist-card-icon">
                  <svg
                    className="booklist-card-icon-svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                </div>
                <div className="booklist-card-content">
                  <h3 className="booklist-card-title">{book.name}</h3>
                  <p className="booklist-card-meta">Click to open</p>
                </div>
                <div className="booklist-card-arrow">
                  <svg
                    className="booklist-card-arrow-svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>

      {/* Inline Styles */}
      <style>{`
        .booklist-dashboard {
          padding: 24px;
          max-width: 1400px;
          margin: 0 auto;
          min-height: 100vh;
          background: #f8fafc;
          color: #1e293b;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .booklist-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 32px;
          padding-bottom: 24px;
          border-bottom: 1px solid #e2e8f0;
          gap: 24px;
          flex-wrap: wrap;
        }

        .booklist-header-title {
          flex: 1;
          min-width: 200px;
        }

        .booklist-title {
          font-size: 2rem;
          font-weight: 800;
          margin: 0 0 8px 0;
          color: #0f172a;
          letter-spacing: -0.02em;
        }

        .booklist-subtitle {
          font-size: 1rem;
          color: #64748b;
          font-weight: 500;
          margin: 0;
        }

        .booklist-search-container {
          width: 100%;
          max-width: 400px;
          min-width: 280px;
        }

        .booklist-content {
          background: #ffffff;
          border-radius: 24px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05);
          padding: 24px;
          min-height: 400px;
        }

        .booklist-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          text-align: center;
          color: #94a3b8;
        }

        .booklist-empty-icon {
          font-size: 4rem;
          margin-bottom: 20px;
          opacity: 0.5;
        }

        .booklist-empty-text {
          font-size: 1.1rem;
          font-weight: 500;
          margin: 0;
        }

        .booklist-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 16px;
        }

        .booklist-card {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          text-decoration: none;
          color: inherit;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
        }

        .booklist-card:hover {
          background: #ffffff;
          border-color: #94a3b8;
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -4px rgba(0, 0, 0, 0.08);
        }

        .booklist-card-icon {
          flex-shrink: 0;
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #0d9488 0%, #115e59 100%);
          border-radius: 12px;
          color: #ffffff;
        }

        .booklist-card-icon-svg {
          width: 24px;
          height: 24px;
        }

        .booklist-card-content {
          flex-grow: 1;
          min-width: 0;
        }

        .booklist-card-title {
          font-size: 1rem;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 4px 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .booklist-card-meta {
          font-size: 0.85rem;
          color: #64748b;
          margin: 0;
          font-weight: 500;
        }

        .booklist-card-arrow {
          flex-shrink: 0;
          color: #94a3b8;
          transition: color 0.2s ease, transform 0.2s ease;
        }

        .booklist-card:hover .booklist-card-arrow {
          color: #0f172a;
          transform: translateX(4px);
        }

        .booklist-card-arrow-svg {
          width: 20px;
          height: 20px;
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .booklist-dashboard {
            padding: 16px;
          }

          .booklist-header {
            flex-direction: column;
            gap: 16px;
          }

          .booklist-title {
            font-size: 1.5rem;
          }

          .booklist-search-container {
            max-width: 100%;
            min-width: auto;
          }

          .booklist-content {
            padding: 16px;
          }

          .booklist-grid {
            grid-template-columns: 1fr;
          }

          .booklist-card {
            padding: 16px;
          }

          .booklist-card-icon {
            width: 40px;
            height: 40px;
          }

          .booklist-card-icon-svg {
            width: 20px;
            height: 20px;
          }
        }
      `}</style>
    </div>
  );
}
